/**
 * Per-MODEL capabilities for Anthropic's server-side tools.
 *
 * Provider kind is not enough. Every Claude model on Claude Platform on AWS
 * accepts the `code_execution_20260120` tool, but **Claude Haiku 4.5 does not
 * support programmatic tool calling** — and a request that carries
 * `allowed_callers` for a model that lacks PTC is rejected OUTRIGHT with a 400:
 *
 *   'claude-haiku-4-5-20251001' does not support programmatic tool calling.
 *   The following tools have `allowed_callers` that require it: find_contact,
 *   find_company, … Explicitly set `allowed_callers=["direct"]` on these tools,
 *   or use a model that supports programmatic tool calling.
 *
 * That is not a degraded turn, it is a DEAD turn: the stream errors before the
 * first step, no assistant message is produced, and the user sees only the
 * generic "We couldn't complete this tool call." Hub `/chat` hit it in
 * production on 2026-08-29 for every message sent while the user's model
 * preference was pinned to Haiku 4.5, while the SAME question answered fine in
 * Slack purely because Slack resolves its model from env (Sonnet 4.6) and never
 * reads the picker. The feature flags were global; the capability was not.
 *
 * So capability is a property of the (model, feature) pair, and it is checked
 * HERE, once, on the way into the request — never inferred from the provider.
 *
 * Source of truth: the published model-compatibility tables for the code
 * execution tool and for programmatic tool calling (docs, 2026-08). Kept as
 * explicit data rather than a regex on the model name because "4.5 and up"
 * is exactly the rule Haiku 4.5 breaks.
 *
 * FAIL-CLOSED for ids we don't know: an unlisted model gets neither feature.
 * Losing code execution on a model nobody has tested is a missing capability;
 * guessing yes is a dead chat for everyone who picks it.
 */

import { logger } from '../logger';

/** What a model can do with Anthropic's server-side sandbox. */
export interface ModelServerToolCapabilities {
  /** Accepts the `code_execution_*` server tool. */
  readonly codeExecution: boolean;
  /**
   * Accepts `allowed_callers` naming the code-execution container (PTC).
   * Requires `codeExecution`; a model can have code execution WITHOUT this
   * (Haiku 4.5 runs the sandbox, it just cannot call our tools from inside it).
   */
  readonly programmaticToolCalling: boolean;
}

const BOTH: ModelServerToolCapabilities = {
  codeExecution: true,
  programmaticToolCalling: true,
};

/** Sandbox yes, PTC no — the Haiku 4.5 shape. */
const CODE_EXECUTION_ONLY: ModelServerToolCapabilities = {
  codeExecution: true,
  programmaticToolCalling: false,
};

/**
 * Model id → capabilities. Keys are the BARE ids we send (the provider expands
 * them to dated ids); dated ids resolve through `normalizeModelId` below.
 */
const MODEL_CAPABILITIES: ReadonlyMap<string, ModelServerToolCapabilities> = new Map([
  // Claude 5 family.
  ['claude-opus-5', BOTH],
  ['claude-sonnet-5', BOTH],
  ['claude-fable-5', BOTH],
  ['claude-mythos-5', BOTH],
  // Claude 4.x.
  ['claude-opus-4-8', BOTH],
  ['claude-opus-4-7', BOTH],
  ['claude-opus-4-6', BOTH],
  ['claude-opus-4-5', BOTH],
  ['claude-sonnet-4-6', BOTH],
  ['claude-sonnet-4-5', BOTH],
  // The exception this module exists for.
  ['claude-haiku-4-5', CODE_EXECUTION_ONLY],
]);

/** Neither feature — unknown ids and every non-Anthropic backend. */
const NONE: ModelServerToolCapabilities = {
  codeExecution: false,
  programmaticToolCalling: false,
};

/**
 * Strip a trailing `-YYYYMMDD` snapshot suffix so `claude-opus-4-5-20251101`
 * and `claude-opus-4-5` resolve to the same entry. Anything else is returned
 * unchanged (trimmed, lowercased).
 */
export function normalizeModelId(modelId: string | null | undefined): string {
  const trimmed = (modelId ?? '').trim().toLowerCase();
  return trimmed.replace(/-\d{8}$/, '');
}

/**
 * Capabilities for `modelId`. Unknown ids get `NONE` — see FAIL-CLOSED above.
 */
export function getModelServerToolCapabilities(
  modelId: string | null | undefined,
): ModelServerToolCapabilities {
  return MODEL_CAPABILITIES.get(normalizeModelId(modelId)) ?? NONE;
}

/** Whether `modelId` accepts the code-execution server tool. */
export function supportsCodeExecution(modelId: string | null | undefined): boolean {
  return getModelServerToolCapabilities(modelId).codeExecution;
}

/** Whether `modelId` accepts `allowed_callers` for the code-execution container. */
export function supportsProgrammaticToolCalling(modelId: string | null | undefined): boolean {
  return getModelServerToolCapabilities(modelId).programmaticToolCalling;
}

/**
 * Warn once per request when a capability is skipped for the active model, so a
 * "why did code execution stop working" question has an answer in the logs
 * instead of silence. Never throws — reporting must not break a turn.
 */
export function logCapabilitySkip(
  feature: 'code_execution' | 'programmatic_tool_calling',
  modelId: string | null | undefined,
  logContext?: Record<string, unknown>,
): void {
  logger.info('server tool skipped: model does not support it', {
    ...logContext,
    op: 'modelCapabilities',
    feature,
    modelId: modelId ?? null,
  });
}
